import React, { useState } from 'react';
import './LazyImage.css';

/**
 * Simple optimized image component without lazy loading
 * Features:
 * - WebP format with fallback support
 * - Loading states
 * - Error handling
 * - No lazy loading (loads immediately)
 */
const SimpleOptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  style = {}, 
  loading = 'eager',
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Generate WebP source from original src
  const getWebPSrc = (originalSrc) => {
    if (!originalSrc) return '';
    
    // If it's already WebP, return as is
    if (originalSrc.includes('.webp')) return originalSrc;
    
    // Replace extension with .webp
    const webpSrc = originalSrc.replace(/\.(png|jpg|jpeg)$/i, '.webp');
    return webpSrc;
  };

  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
  };

  const handleError = () => {
    setHasError(true);
  };

  const webpSrc = getWebPSrc(src);

  return (
    <div 
      className={`lazy-image-container ${className} ${hasError ? 'lazy-image-error' : ''}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        ...style
      }}
    >
      {/* Loading placeholder */}
      {!isLoaded && !hasError && (
        <div 
          className="lazy-image-shimmer"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            borderRadius: 'inherit',
          }}
        />
      )}

      {/* Error state */}
      {hasError && (
        <div 
          className="lazy-image-error-message"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#666',
            fontSize: '14px',
            zIndex: 3,
          }}
        >
          Failed to load image
        </div>
      )}

      {/* Optimized image - loads immediately */}
      {!hasError && (
        src.includes('.webp') ? (
          // Direct WebP image (no fallback needed)
          <img
            src={src}
            alt={alt}
            className="lazy-image"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: isLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
              position: 'relative',
              zIndex: 2,
              borderRadius: 'inherit',
            }}
            onLoad={handleLoad}
            onError={handleError}
            loading={loading}
            decoding="async"
            {...props}
          />
        ) : (
          // PNG/JPEG with WebP optimization
          <picture>
            {/* WebP version for supported browsers */}
            <source 
              srcSet={webpSrc}
              type="image/webp"
            />
            
            {/* Fallback for browsers that don't support WebP */}
            <img
              src={src}
              alt={alt}
              className="lazy-image"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: isLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out',
                position: 'relative',
                zIndex: 2,
                borderRadius: 'inherit',
              }}
              onLoad={handleLoad}
              onError={handleError}
              loading={loading}
              decoding="async"
              {...props}
            />
          </picture>
        )
      )}
    </div>
  );
};

export default SimpleOptimizedImage;
